import React from 'react'
import { observer } from 'mobx-react-lite'

import Stack from '@mui/material/Stack'

import { Text } from '@mxenabled/mxui'

import {
  ANALYTICS_EVENTS,
  ConnectDrawer,
  Loader,
  WidgetContainer,
  useAccountStore,
  useEvent,
  useAppConfig,
  useGlobalUiStore,
  useGlobalCopyStore,
  useScreenSize,
  useWidgetLoadTimer,
} from '../common'
import { WidgetProps } from '../core'

import { SkeletonLoader } from '../common/components/SkeletonLoader'

import AccountsList from './components/AccountsList'
import ConnectionsDrawer from './components/ConnectionsDrawer'

const AccountsWidget: React.FC<WidgetProps> = ({ onBackClick, onMenuClick, sx = {} }) => {
  const [showConnectWidget, setShowConnectWidget] = React.useState(false)
  const [showConnectionsWidget, setShowConnectionsWidget] = React.useState(false)

  const { config } = useAppConfig()
  const { isAccountDataLoaded, loadAccountData } = useAccountStore()
  const { accounts: copy, connect: connectCopy } = useGlobalCopyStore()
  const { isInitialized, selectedAccountGuids } = useGlobalUiStore()
  const { isDesktop } = useScreenSize()
  const { onEvent } = useEvent()

  useWidgetLoadTimer({
    widgetName: 'AccountsWidget',
    isLoaded: isAccountDataLoaded,
  })

  React.useEffect(() => {
    if (!isAccountDataLoaded) {
      loadAccountData().finally()
    }
  }, [])

  const handleAddAccount = () => {
    setShowConnectWidget(true)
    onEvent(ANALYTICS_EVENTS.ACCOUNTS_CLICK_CONNECT)
  }

  const handleManageConnections = () => {
    setShowConnectionsWidget(true)
    onEvent(ANALYTICS_EVENTS.ACCOUNTS_CLICK_CONNECT)
  }

  if (!isInitialized) return <Loader />

  return (
    <WidgetContainer
      actions={
        config.show_connections_widget_in_master
          ? [
              { iconName: 'add', label: copy.add, onClick: handleAddAccount },
              {
                iconName: 'credit_card_gear',
                label: copy.manage,
                onClick: handleManageConnections,
              },
            ]
          : []
      }
      hasDivider={true}
      onAccountsFilterClick={() => onEvent(ANALYTICS_EVENTS.ACCOUNTS_CLICK_FILTER)}
      onBackClick={onBackClick}
      onMenuClick={onMenuClick}
      sx={sx}
      title={copy.title}
    >
      {!isAccountDataLoaded ? (
        <SkeletonLoader sx={{ px: isDesktop ? 48 : 24, pt: 24 }} variant="list" />
      ) : (
        <Stack>
          {selectedAccountGuids.length < 1 && (
            <Text display="flex" justifyContent="center" my={24}>
              {copy.no_accounts}
            </Text>
          )}
          <AccountsList />
        </Stack>
      )}

      <ConnectDrawer
        onClose={() => setShowConnectWidget(false)}
        showConnectWidget={showConnectWidget}
        title={connectCopy.mini_title}
      />
      <ConnectionsDrawer
        onClose={() => setShowConnectionsWidget(false)}
        showConnectionsWidget={showConnectionsWidget}
        title={copy.manage_connections}
      />
    </WidgetContainer>
  )
}

export default observer(AccountsWidget)
